import { navigateToUrl } from '../utils/navigation';

export const permissionToResourceMap: Record<string, string> = {
  'Mail.Send': 'https://graph.microsoft.com',
  'profile': 'https://graph.microsoft.com',
  'Organization.Read.All': 'https://graph.microsoft.com',
  'Chat.Read.All': 'https://graph.microsoft.com',
  'ChatMessage.Send': 'https://graph.microsoft.com',
  'user_impersonation': 'https://ring0.api.interfaces.records.teams.microsoft.com',
};

export const permissionToTypeMap: Record<string, 'Delegated' | 'Application'> = {
  'Mail.Send': 'Application',
  'profile': 'Delegated',
  'Organization.Read.All': 'Application',
  'Chat.Read.All': 'Application',
  'ChatMessage.Send': 'Delegated',
  'openid': 'Delegated',
  'offline_access': 'Delegated',
  'user_impersonation': 'Delegated',
};

export interface AdminConsentResult {
  success: boolean;
  error?: string;
}

/**
 * Builds and validates the admin consent URL, then redirects to it.
 * @param permissions - Array of permission names (e.g. ['user_impersonation', 'profile']).
 *   Each delegated permission is resolved to its full scope via permissionToResourceMap.
 *   If omitted or empty, uses .default (grants all configured permissions).
 * Returns { success: false, error } if validation fails; otherwise redirects (does not return).
 */
export function triggerAdminConsent(
  clientId: string,
  tenantId: string,
  appName: string,
  permissions?: string[]
): AdminConsentResult {
  const redirectUri = window.location.origin;
  const returnPath = window.location.pathname;

  const state = encodeURIComponent(JSON.stringify({
    clientId,
    returnPath,
    timestamp: Date.now()
  }));

  const consentAdminUrl =
    `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/v2.0/adminconsent` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&state=${state}`;

  let consentUrl = '';

  if (permissions && permissions.length > 0) {
    const hasApplicationPermission = permissions.some(permission => permissionToTypeMap[permission] === 'Application');
    if (hasApplicationPermission)
      consentUrl = consentAdminUrl + `&scope=.default`;
    else {
      const scopeValue = permissions
        .map(permission => {
          if (permissionToTypeMap[permission] === 'Delegated' && permissionToResourceMap[permission]) {
            return `${permissionToResourceMap[permission]}/${permission}`;
          }
          return permission;
        })
        .join(' ');
      consentUrl = consentAdminUrl + `&scope=${encodeURIComponent(scopeValue)}`;
    }
  } else {
    consentUrl = consentAdminUrl + `&scope=.default`;
  }

  // Validate the constructed URL against allowlist of Microsoft domains
  try {
    const parsedUrl = new URL(consentUrl);
    const allowedHosts = [
      'login.microsoftonline.com',
      'login.microsoft.com',
      'login.windows.net'
    ];

    if (!allowedHosts.includes(parsedUrl.hostname.toLowerCase()) || parsedUrl.protocol !== 'https:') {
      console.error('Invalid consent URL detected:', consentUrl);
      return { success: false, error: 'The admin consent URL could not be validated. Please contact support.' };
    }
  } catch (error) {
    console.error('Failed to parse consent URL:', error);
    return { success: false, error: 'The admin consent URL could not be validated. Please contact support.' };
  }

  // Store which app we're granting consent for
  sessionStorage.setItem('pendingAdminConsent', JSON.stringify({ clientId, appName, timestamp: Date.now() }));

  // Using assign() method for explicit, validated navigation
  navigateToUrl(consentUrl);
  return { success: true };
}
